import React from "react"
import Footer from "../components/footer"
import Navbar from "../components/nav"

function ClashTrophyPolicy() {
  return (
    <>
      <div>
        <header className="header-area">
          <div className="navbar-area">
            <div className="container">
              <div className="row">
                <div className="col-lg-12">
                  <Navbar />
                </div>
              </div>
            </div>
          </div>
        </header>

        <div id="home">
          <div className="container mt-125 mb-50">
            <div className="">
              <div className="card-body shadow">
                <h1>Privacy Policy</h1>
                <h4 className="mt-3">ClashTrophy - Clash Royale Stats & Deck Builder</h4>
                <p className="mt-2"><strong>Last Updated: September 02, 2026</strong></p>
                <p><strong>Version: 1.0</strong></p>
                <p className="mt-2"><strong>Developer:</strong> MWX Apps</p>
                <p><strong>App Name:</strong> ClashTrophy</p>
                <p><strong>Package Name:</strong> com.clashtrophy</p>
                <p><strong>Contact Email:</strong> web-developer@outlook.com</p>

                <hr className="my-4" />

                {/* Introduction */}
                <h4 className="mt-4" id="introduction">Introduction</h4>
                <p className="mt-3">
                  Welcome to ClashTrophy ("we," "our," or "us"). We are committed to
                  protecting your privacy and ensuring you have a positive experience when
                  using our ClashTrophy mobile application (the "App"). This Privacy Policy explains how we handle information
                  when you use the App to view live Clash Royale stats, build decks, track clan wars, and explore win rates & meta tiers.
                </p>
                <p className="mt-3">
                  <strong className="text-primary">
                    Our Core Privacy Principle: We do not collect personal data. Your searches stay on your device.
                  </strong>
                </p>
                <p className="mt-3">
                  ClashTrophy is designed for Clash Royale players to analyze public game data. All player statistics, battle history, deck win rates and clan war information displayed in the App are <strong>public game data</strong> provided by the official Clash Royale API and community APIs (e.g., RoyaleAPI). We do not require account registration and we do not link game data to your identity.
                </p>

                <hr className="my-4" />

                {/* Information We Do NOT Collect */}
                <h4 className="mt-4" id="not-collected">Information We Do NOT Collect</h4>
                <p className="mt-3">
                  ClashTrophy is designed with privacy as a fundamental principle.
                  We want to be completely transparent about what we do NOT do:
                </p>

                <h5 className="mt-4">Personal Information</h5>
                <ul className="mt-2" style={{ listStyle: "none", paddingLeft: "10px" }}>
                  <li>❌ We do NOT collect your name, email, phone number or contact information</li>
                  <li>❌ We do NOT require account registration or login</li>
                  <li>❌ We do NOT collect your location</li>
                  <li>❌ We do NOT collect device identifiers for tracking or profiling</li>
                  <li>❌ We do NOT sell any data to third parties</li>
                  <li>❌ We do NOT link your Clash Royale player tag to your real identity</li>
                </ul>

                <h5 className="mt-4">Tracking & Analytics</h5>
                <ul className="mt-2" style={{ listStyle: "none", paddingLeft: "10px" }}>
                  <li>❌ We do NOT use Firebase Analytics, Google Analytics or behavioral tracking SDKs</li>
                  <li>❌ We do NOT track which player tags you search for analytics purposes</li>
                  <li>❌ We do NOT build advertising profiles</li>
                  <li>❌ We do NOT use cross-app tracking</li>
                </ul>

                <hr className="my-4" />

                {/* How the App Works */}
                <h4 className="mt-4" id="how-it-works">How the App Works</h4>

                <h5 className="mt-4">Public Game Data</h5>
                <p className="mt-2">
                  ClashTrophy does <strong>not generate</strong> its own player data. When you enter a Clash Royale player tag (e.g., #ABC123), clan tag or search for a deck:
                </p>
                <ul className="mt-2">
                  <li>The App sends the tag/query to the official Clash Royale API / RoyaleAPI via HTTPS</li>
                  <li>The API returns public data: trophies, arena, battle history, deck usage, win rates, clan war results, card levels, meta tier lists</li>
                  <li>This data is displayed in the App and optionally cached locally for faster loading</li>
                  <li><strong>We do not store this data on our servers</strong> — we do not operate a backend that saves your search history</li>
                </ul>
                <p className="mt-2">
                  <strong>Important:</strong> Player tags, battle logs and clan data are <strong>public information</strong> made available by Supercell for any player who has not opted for private profile. ClashTrophy only displays what is already public.
                </p>

                <h5 className="mt-4">Local Processing & Caching</h5>
                <ul className="mt-2">
                  <li><strong>Deck Builder:</strong> Decks you create in the deck builder are stored <strong>locally on your device</strong> only</li>
                  <li><strong>Favorites & History:</strong> Your favorite players, clans, decks and recent searches are saved in local storage (SharedPreferences / Room) for quick access</li>
                  <li><strong>Meta Tiers & Win Rates:</strong> Aggregated meta statistics are fetched from public APIs and cached temporarily on device, then refreshed periodically</li>
                  <li><strong>No Account Sync:</strong> No cloud sync. If you uninstall the App, all local favorites and history are deleted. If you change device, you need to search again</li>
                </ul>

                <h5 className="mt-4">Offline Capability</h5>
                <p className="mt-2">
                  Cached player profiles, decks and meta tiers can be viewed offline after they have been loaded once. However, live stats (current trophies, recent battles, clan wars) require an internet connection to fetch fresh data.
                </p>

                <hr className="my-4" />

                {/* Data Stored on Your Device */}
                <h4 className="mt-4" id="local-data">Data Stored on Your Device</h4>
                <p className="mt-3">
                  The following data is stored <strong>locally on your device only</strong> and never transmitted to our servers:
                </p>

                <h5 className="mt-4">1. Search & Favorites</h5>
                <ul className="mt-2">
                  <li>Player tags you search for and mark as favorite</li>
                  <li>Clan tags you follow</li>
                  <li>Recent search history (player, clan, deck searches)</li>
                  <li>Used to provide quick access and auto-complete — you can clear this anytime via Settings &gt; Clear History</li>
                </ul>

                <h5 className="mt-4">2. Deck Builder Data</h5>
                <ul className="mt-2">
                  <li>Custom decks you create (8-card combinations) and their names</li>
                  <li>Deck win-rate analysis results calculated locally from public meta data</li>
                  <li>Favorite meta decks you save</li>
                </ul>

                <h5 className="mt-4">3. Clan War Tracker Preferences</h5>
                <ul className="mt-2">
                  <li>Tracked clans for war notification and war log caching</li>
                  <li>War history cached for offline viewing</li>
                </ul>

                <h5 className="mt-4">4. App Preferences</h5>
                <ul className="mt-2">
                  <li>Theme settings (light/dark)</li>
                  <li>Language preferences</li>
                  <li>Notification preferences for clan war reminders (if enabled)</li>
                </ul>

                <h5 className="mt-4">How to Delete Your Data</h5>
                <p className="mt-2">You can delete all locally stored data by:</p>
                <ol className="mt-2">
                  <li>Using "Clear History" / "Clear Favorites" within the App Settings</li>
                  <li>Clearing the App's data in your device Settings &gt; Apps &gt; ClashTrophy &gt; Storage &gt; Clear Data</li>
                  <li>Uninstalling the application — Android automatically removes all App data</li>
                </ol>

                <hr className="my-4" />

                {/* Information We Do Collect - Minimal */}
                <h4 className="mt-4" id="collected">Information Transmitted (Minimal & Public Only)</h4>
                <p className="mt-3">
                  To provide live stats, the App must transmit <strong>only</strong> the following to public Clash Royale APIs:
                </p>
                <ul className="mt-2">
                  <li><strong>Player Tag / Clan Tag / Search Query:</strong> The tag or keyword you explicitly enter (e.g., "#2PP", "Mega Knight meta deck"). This is public game identifier, not personal information.</li>
                  <li><strong>No personal data attached:</strong> We do not attach your name, email, location, device ID or advertising ID to these API requests.</li>
                  <li><strong>API Response:</strong> Public stats returned (trophies, wins/losses, cards, war results) are displayed and cached locally only.</li>
                </ul>
                <p className="mt-2">
                  Example API call: <code>GET https://api.clashroyale.com/v1/players/%23ABC123</code> via our proxy / RoyaleAPI — only the tag is sent over HTTPS.
                </p>

                <hr className="my-4" />

                {/* Permissions We Request */}
                <h4 className="mt-4" id="permissions">Permissions We Request</h4>

                <h5 className="mt-4">Internet Permission (Required)</h5>
                <ul className="mt-2">
                  <li><strong>Purpose:</strong> To fetch live Clash Royale stats, deck meta tiers, win rates and clan war data from official/community APIs</li>
                  <li><strong>Usage:</strong> HTTPS requests only when you search or refresh stats</li>
                  <li><strong>Data Transmitted:</strong> Only player/clan tags and deck queries — no personal data</li>
                </ul>

                <h5 className="mt-4">Network State Permission</h5>
                <ul className="mt-2">
                  <li><strong>Purpose:</strong> To check if device is online before attempting API calls and to show appropriate offline message</li>
                </ul>

                <h5 className="mt-4">Storage Permission (Optional - Android 12 and below)</h5>
                <ul className="mt-2">
                  <li><strong>Purpose:</strong> Only if you choose to export deck images or share stats as image — to save image to gallery</li>
                  <li><strong>Scope:</strong> Write-only when you tap Share/Export — no access to your photos otherwise</li>
                </ul>

                <h5 className="mt-4">Notifications Permission (Optional)</h5>
                <ul className="mt-2">
                  <li><strong>Purpose:</strong> If you enable clan war reminders, we schedule local notifications on your device</li>
                  <li><strong>Local Only:</strong> Notifications are scheduled locally — we do not send push notifications from a server and do not collect push tokens for tracking</li>
                  <li><strong>Control:</strong> You can disable at any time via App Settings or system Settings &gt; Notifications</li>
                </ul>

                <hr className="my-4" />

                {/* Third-Party Services */}
                <h4 className="mt-4" id="third-party">Third-Party Services</h4>

                <h5 className="mt-4">Clash Royale API & RoyaleAPI</h5>
                <p className="mt-2">
                  The App uses the official <a href="https://developer.clashroyale.com/" target="_blank" rel="noopener noreferrer">Supercell Clash Royale API</a> and community <a href="https://royaleapi.com/" target="_blank" rel="noopener noreferrer">RoyaleAPI</a> to retrieve public game data.
                </p>
                <ul className="mt-2">
                  <li><strong>Data Sent:</strong> Player tag, clan tag, deck search queries only</li>
                  <li><strong>Privacy:</strong> Please refer to <a href="https://supercell.com/en/privacy-policy/" target="_blank" rel="noopener noreferrer">Supercell Privacy Policy</a> for how Supercell handles game data</li>
                  <li><strong>We do not control</strong> these APIs — they are operated by Supercell and RoyaleAPI</li>
                </ul>

                <h5 className="mt-4">We Do NOT Use:</h5>
                <ul className="mt-2">
                  <li>Google Analytics or Firebase Analytics</li>
                  <li>Facebook SDK or social media tracking SDKs</li>
                  <li>Advertising networks that profile you (no AdMob personalized ads — if ads are shown, they are non-personalized)</li>
                  <li>Crash reporting that collects personal identifiers</li>
                  <li>Any third-party tracking services</li>
                </ul>

                <h5 className="mt-4">Google Play Services</h5>
                <p className="mt-2">
                  The App is distributed through Google Play Store, which has its own privacy policy.
                  Please refer to <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                  Google's Privacy Policy</a> for information about how Google handles data related to app distribution.
                </p>

                <hr className="my-4" />

                {/* Children's Privacy */}
                <h4 className="mt-4" id="childrens-privacy">Children's Privacy</h4>
                <p className="mt-3">
                  ClashTrophy does not knowingly collect any personal information from
                  children under the age of 13. Since we do not collect personal information
                  from any users, this applies to all age groups equally. Clash Royale is a game rated for players 13+ on some stores, but our App does not gate by age because we collect no personal data. If you are a parent and believe your child has provided personal information to us, please contact us at web-developer@outlook.com and we will assist.
                </p>

                <hr className="my-4" />

                {/* Data Security */}
                <h4 className="mt-4" id="security">Data Security</h4>
                <p className="mt-3">Since all favorites, history and deck data remain on your device:</p>
                <ul className="mt-2">
                  <li>Your local data is protected by Android's app sandboxing — other apps cannot access it</li>
                  <li>API requests use HTTPS encryption — player tags are transmitted securely</li>
                  <li>No personal data is transmitted, so there is no risk of personal data interception</li>
                  <li>We recommend using device encryption and screen lock to protect your device</li>
                  <li>We do not operate servers storing your data, so there is no server breach risk for your personal history</li>
                </ul>

                <hr className="my-4" />

                {/* Your Rights */}
                <h4 className="mt-4" id="your-rights">Your Rights</h4>
                <p className="mt-3">You have complete control over your data:</p>
                <ul className="mt-2">
                  <li><strong>Access:</strong> All your favorites, history and decks are on your device and accessible via the App</li>
                  <li><strong>Deletion:</strong> Clear history/favorites anytime within the App or via system settings</li>
                  <li><strong>Portability:</strong> Your data exists only locally — no export to servers needed</li>
                  <li><strong>Opt-out:</strong> Do not enter a player tag if you do not want to fetch public stats. No tracking to opt out of</li>
                  <li><strong>Supercell Data:</strong> To make your Clash Royale profile private, use settings in the Clash Royale game itself (Settings &gt; Privacy)</li>
                </ul>

                <hr className="my-4" />

                {/* International Users */}
                <h4 className="mt-4" id="international">International Users</h4>
                <p className="mt-3">
                  ClashTrophy can be used anywhere in the world. Since we do not collect personal data:
                </p>
                <ul className="mt-2">
                  <li>No cross-border transfer of personal data occurs (only public game tags are sent to APIs)</li>
                  <li>GDPR, CCPA and other privacy regulations are inherently satisfied as no personal data is processed</li>
                  <li>Your search history and decks stay in your jurisdiction (your device)</li>
                </ul>

                <hr className="my-4" />

                {/* Changes to This Privacy Policy */}
                <h4 className="mt-4" id="changes">Changes to This Privacy Policy</h4>
                <p className="mt-3">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by:
                </p>
                <ul className="mt-2">
                  <li>Updating the "Last Updated" date at the top of this policy</li>
                  <li>Posting the new Privacy Policy on this page (https://mwx-apps.web.app/clashtrophy/privacy-policy)</li>
                  <li>Notifying through app updates when significant changes occur</li>
                </ul>
                <p className="mt-3">
                  We encourage you to review this Privacy Policy periodically for any changes.
                </p>

                <hr className="my-4" />

                {/* Contact Us */}
                <h4 className="mt-4" id="contact">Contact Us</h4>
                <p className="mt-3">
                  If you have any questions about this Privacy Policy or our privacy practices,
                  please contact us at:
                </p>
                <p className="mt-2"><strong>Email:</strong> web-developer@outlook.com</p>
                <p><strong>Developer:</strong> MWX Apps</p>
                <p><strong>Website:</strong> https://mwx-apps.web.app/clashtrophy</p>

                <hr className="my-4" />

                {/* Summary Table */}
                <h4 className="mt-4" id="summary">Summary</h4>
                <div className="table-responsive mt-3">
                  <table className="table table-bordered">
                    <thead className="thead-light">
                      <tr>
                        <th>Data Type</th>
                        <th>Collected?</th>
                        <th>Shared?</th>
                        <th>Stored Where?</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Personal Info (name, email, location)</td>
                        <td>❌ No</td>
                        <td>❌ No</td>
                        <td>N/A</td>
                      </tr>
                      <tr>
                        <td>Player/Clan Tags you enter</td>
                        <td>✅ Sent to API only</td>
                        <td>✅ Public API only</td>
                        <td>Local cache only</td>
                      </tr>
                      <tr>
                        <td>Battle History / Win Rates / Meta Tiers</td>
                        <td>❌ Public game data</td>
                        <td>❌ No</td>
                        <td>Fetched & cached locally</td>
                      </tr>
                      <tr>
                        <td>Deck Builder Decks</td>
                        <td>✅ Local only</td>
                        <td>❌ No</td>
                        <td>Your device</td>
                      </tr>
                      <tr>
                        <td>Favorites / Recent Searches</td>
                        <td>✅ Local only</td>
                        <td>❌ No</td>
                        <td>Your device</td>
                      </tr>
                      <tr>
                        <td>Location / Tracking / Analytics</td>
                        <td>❌ No</td>
                        <td>❌ No</td>
                        <td>N/A</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <hr className="my-4" />

                <div className="text-center mt-4">
                  <h5 className="text-primary">ClashTrophy - Live Stats, Deck Builder & Clan War Tracker</h5>
                  <p className="mt-2"><em>This privacy policy is effective as of September 02, 2026.</em></p>
                  <p className="mt-1"><em>Clash Royale is a trademark of Supercell. ClashTrophy is not affiliated with, endorsed or sponsored by Supercell.</em></p>
                </div>

              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  )
}

export default ClashTrophyPolicy
